import { useEffect, useMemo } from 'react';
import { Routes, useLocation, Route, Navigate, Link } from 'react-router-dom';
import {
  HomeIcon,
  PageLayout,
  PageLayoutSidebarItem,
  PageLayoutSidebarItemContent,
  Paper,
  Spinner,
  StarCircled,
  UnlockedIcon,
} from '@weareopenr/component-library';
import styles from './Layout.module.scss';
import { Dashboard } from '../../pages/Dashboard/Dashboard';
import { Brands } from 'src/pages/Brands/Brands';
import { Brand } from 'src/pages/Brand/Brand';
import { Users } from 'src/pages/Users/Users';
import { Roles } from 'src/pages/Roles/Roles';
import { User } from 'src/pages/User/User';
import { useAuthWrapper } from '@weareopenr/authkit';
import { usePermissions, useUserTracking } from 'src/hooks';
import ProtectedRoute from 'src/components/ProtectedRoute';

const handleAdminSwitch = () => {
  const adminUrl = globalThis.location.origin.replace('admin', 'hub');
  globalThis.location.href = adminUrl;
};

const Layout = () => {
  const { pathname } = useLocation();
  const { user, isLoading, role, signOut } = useAuthWrapper();
  const { canViewRoles, canViewUsers } = usePermissions();
  const { identifyUser } = useUserTracking();

  useEffect(() => {
    if (user?.id) {
      identifyUser(user.id, {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      });
    }
  }, [user, identifyUser]);

  const userMenu = useMemo(
    () => ({
      name:
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : user?.email ?? '',
      userRole: role ?? '',
      switchAppName: 'Hub',
      isAdminButtonEnabled: true,
      onSwitchApp: handleAdminSwitch,
      onLogout: signOut,
    }),
    [user, role, signOut],
  );

  if (isLoading)
    return (
      <div className={styles.spinner}>
        <Spinner size="xlarge" colour="rainbow" />
      </div>
    );

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const accessControlContent: PageLayoutSidebarItemContent[] = [
    ...(canViewUsers ? [{ label: 'Users', path: '/users' }] : []),
    ...(canViewRoles ? [{ label: 'Roles', path: '/roles' }] : []),
  ];

  const sidebarContent: PageLayoutSidebarItem[] = [
    {
      title: 'Account',
      IconComponent: StarCircled,
      content: [
        {
          label: 'Brands',
          path: '/brands',
        },
      ],
    },
    ...(accessControlContent.length > 0
      ? [
          {
            title: 'Access Control',
            IconComponent: UnlockedIcon,
            content: accessControlContent,
          },
        ]
      : []),
  ];

  return (
    <PageLayout
      appName="Admin"
      homeLinks={[
        {
          title: 'Dashboard',
          path: '/',
          IconComponent: HomeIcon,
        },
      ]}
      sidebarItems={sidebarContent}
      currentPath={pathname}
      LinkComponent={Link}
      userMenu={userMenu}
    >
      <Paper fullHeight>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/brands" element={<Brands />} />
          <Route path="/brands/:brandId" element={<Brand />} />
          <Route
            path="/users"
            element={<ProtectedRoute element={<Users />} hasPermission={canViewUsers} />}
          />
          <Route
            path="/users/:userId"
            element={<ProtectedRoute element={<User />} hasPermission={canViewUsers} />}
          />
          <Route
            path="/roles"
            element={<ProtectedRoute element={<Roles />} hasPermission={canViewRoles} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Paper>
    </PageLayout>
  );
};

export default Layout;
